import { useRoute, Link } from "wouter";
import { ArrowLeft, Printer } from "lucide-react";
import { useChild } from "../lib/api/useChildren";
import { useAllergies } from "../lib/api/useAllergies";
import { useMedications } from "../lib/api/useMedications";
import { useImmunizations } from "../lib/api/useImmunizations";
import { useMedicalHistory } from "../lib/api/useMedicalHistory";
import { useAuthorizedPickups } from "../lib/api/useAuthorizedPickups";
import { useCareAuthorization } from "../lib/api/useCareAuthorization";
import { Button } from "../components/ui/Button";
import { PageLoader } from "../components/ui/Spinner";
import { FREQUENCY_LABELS } from "@shared/constants/frequency";
import { SEVERITY_LABELS } from "@shared/constants/severity";
import { PICKUP_CONSENT_ENABLED } from "@shared/constants/features";

/**
 * Emergency Information Form — the one-page medical summary standardised by the
 * AAP and ACEP for children with special health care needs, and the format
 * emergency staff recognise on sight.
 *
 * It exists for exactly this product's scenario: a child presents with an acute
 * problem "at a time when neither parent nor pediatrician is immediately
 * available". Everything here is composed from data the owner already has, so
 * there is NO new endpoint and no new scope surface — it reuses the same
 * authenticated hooks as the child detail page.
 *
 * Print-first by design. Nothing on this page is wrapped in Reveal or any
 * scroll-triggered animation, which can print blank (see components/ui/Reveal.tsx).
 */

function fmtDate(value: string | null): string {
  if (!value) return "—";
  // Calendar dates arrive as YYYY-MM-DD; parse as UTC so the displayed day can
  // never slip backwards in a negative-offset timezone.
  const d = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(d);
}

const dash = (v: string | null | undefined) => (v && v.length > 0 ? v : "—");

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-4 break-inside-avoid border border-neutral-300">
      <h2 className="border-b border-neutral-300 bg-neutral-100 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-neutral-800">
        {title}
      </h2>
      <div className="p-3 text-sm">{children}</div>
    </section>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-2 py-0.5">
      <span className="w-40 flex-shrink-0 text-xs font-semibold uppercase tracking-wide text-neutral-600">
        {label}
      </span>
      <span className="min-w-0 flex-1 text-neutral-900">{value}</span>
    </div>
  );
}

export function EmergencyInfoForm() {
  const [, params] = useRoute("/child/:id/eif");
  const childId = params?.id;

  const { data: child, isLoading } = useChild(childId);
  const { data: allergies } = useAllergies(childId);
  const { data: medications } = useMedications(childId);
  const { data: immunizations } = useImmunizations(childId);
  const { data: history } = useMedicalHistory(childId);
  // Both hooks stay idle while PICKUP_CONSENT_ENABLED is off (their routes are
  // not mounted), and every row they feed is left out of the form below.
  const { data: pickups } = useAuthorizedPickups(childId);
  const { data: authorization } = useCareAuthorization(childId);

  if (isLoading || !child) return <PageLoader />;

  const rescueNameFor = (rescueMedicationId: string | null) =>
    rescueMedicationId
      ? (medications?.find((m) => m.id === rescueMedicationId)?.name ?? null)
      : null;

  return (
    <div className="mx-auto max-w-[8.5in] bg-white p-6 text-neutral-900 print:p-0">
      <div className="no-print mb-4 flex items-center justify-between">
        <Link href={`/child/${child.id}`}>
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Back to profile
          </Button>
        </Link>
        <Button size="sm" onClick={() => window.print()}>
          <Printer className="h-4 w-4" aria-hidden />
          Print
        </Button>
      </div>

      <header className="border-b-2 border-neutral-800 pb-2">
        <h1 className="text-lg font-bold uppercase tracking-wide">
          Emergency Information Form
        </h1>
        <p className="text-xs text-neutral-600">
          Modeled on the American Academy of Pediatrics / American College of
          Emergency Physicians Emergency Information Form. Generated{" "}
          {fmtDate(new Date().toISOString())} — verify with a parent or guardian
          before relying on it.
        </p>
      </header>

      <Section title="Child">
        <Row label="Nickname" value={child.name} />
        {/* BRIEF §5.2 decision: the EIF is the parent's OWN printout (not a
            care link) and exists for the ER, where a legal name is what the
            hospital matches records on, so it prints whenever one is set. The
            row is omitted otherwise, leaving the form as it was before v2. */}
        {child.legalName && <Row label="Legal name" value={child.legalName} />}
        <Row label="Age" value={child.age === null ? "—" : String(child.age)} />
        <Row label="Grade" value={dash(child.grade)} />
        <Row label="School" value={dash(child.schoolName)} />
        <Row label="School phone" value={dash(child.schoolPhone)} />
        <Row label="Teacher" value={dash(child.teacherName)} />
        <Row label="School nurse" value={dash(child.schoolNursePhone)} />
        <Row label="Blood type" value={dash(history?.bloodType)} />
      </Section>

      <Section title="Parents / Guardians & Emergency Contacts">
        <Row
          label="Primary contact"
          value={`${dash(child.emergencyContactName)}${
            child.emergencyContactRelationship
              ? ` (${child.emergencyContactRelationship})`
              : ""
          } · ${dash(child.emergencyContactPhone)}`}
        />
        <Row
          label="Secondary contact"
          value={`${dash(child.secondaryEmergencyContactName)}${
            child.secondaryEmergencyContactRelationship
              ? ` (${child.secondaryEmergencyContactRelationship})`
              : ""
          } · ${dash(child.secondaryEmergencyContactPhone)}`}
        />
      </Section>

      <Section title="Physicians">
        <Row
          label="Primary physician"
          value={`${dash(history?.primaryPhysicianName)} · ${dash(history?.primaryPhysicianPhone)}`}
        />
        <Row
          label="Dentist"
          value={`${dash(history?.dentistName)} · ${dash(history?.dentistPhone)}`}
        />
        {/* From the consent & insurance record: off with Pickup & consent, so
            the form never prints "—" over a hospital the parent did set. */}
        {PICKUP_CONSENT_ENABLED && (
          <Row label="Preferred hospital" value={dash(authorization?.preferredHospital)} />
        )}
      </Section>

      <Section title="Diagnoses / Conditions">
        <p className="whitespace-pre-wrap">{dash(history?.conditions)}</p>
        {history?.pastIllnesses && (
          <Row label="Past illnesses" value={history.pastIllnesses} />
        )}
        {history?.hospitalizations && (
          <Row label="Hospitalizations" value={history.hospitalizations} />
        )}
      </Section>

      <Section title="Medications">
        {medications && medications.length > 0 ? (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-neutral-300 text-xs uppercase text-neutral-600">
                <th className="py-1 pr-2 font-semibold">Medication</th>
                <th className="py-1 pr-2 font-semibold">Dose</th>
                <th className="py-1 pr-2 font-semibold">Frequency</th>
                <th className="py-1 font-semibold">Expires</th>
              </tr>
            </thead>
            <tbody>
              {medications.map((m) => (
                <tr key={m.id} className="border-b border-neutral-200 last:border-0">
                  <td className="py-1 pr-2">{m.name}</td>
                  <td className="py-1 pr-2">{m.dosage}</td>
                  <td className="py-1 pr-2">{FREQUENCY_LABELS[m.frequency]}</td>
                  <td className="py-1">{fmtDate(m.expiresAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>None on file.</p>
        )}
      </Section>

      <Section title="Allergies & Emergency Care Plan">
        {allergies && allergies.length > 0 ? (
          <ul className="space-y-2">
            {allergies.map((a) => {
              const rescue = rescueNameFor(a.rescueMedicationId);
              return (
                <li key={a.id} className="break-inside-avoid border-l-2 border-neutral-400 pl-2">
                  <p className="font-semibold">
                    {a.name}
                    <span className="ml-2 text-xs font-normal uppercase text-neutral-600">
                      {SEVERITY_LABELS[a.severity]}
                    </span>
                  </p>
                  {a.description && <p className="text-xs">{a.description}</p>}
                  {a.reactionSymptoms && (
                    <p className="text-xs">
                      <span className="font-semibold">Signs: </span>
                      {a.reactionSymptoms}
                    </p>
                  )}
                  {a.treatment && (
                    <p className="text-xs">
                      <span className="font-semibold">Treatment: </span>
                      {a.treatment}
                    </p>
                  )}
                  {rescue && (
                    <p className="text-xs">
                      <span className="font-semibold">Rescue medication: </span>
                      {rescue}
                    </p>
                  )}
                  {a.epinephrineLocation && (
                    <p className="text-xs">
                      <span className="font-semibold">Device kept: </span>
                      {a.epinephrineLocation}
                    </p>
                  )}
                  {a.severity === "severe" && !rescue && (
                    <p className="text-xs font-semibold text-red-700">
                      No rescue medication on file for this severe allergy.
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <p>No known allergies.</p>
        )}
      </Section>

      <Section title="Immunizations">
        {immunizations && immunizations.length > 0 ? (
          <ul className="columns-2 text-xs">
            {immunizations.map((i) => (
              <li key={i.id} className="break-inside-avoid py-0.5">
                <span className="font-semibold">{i.vaccineName}</span> ·{" "}
                {fmtDate(i.dateAdministered)}
                {i.nextDueDate ? ` (next ${fmtDate(i.nextDueDate)})` : ""}
              </li>
            ))}
          </ul>
        ) : (
          <p>None on file.</p>
        )}
      </Section>

      {/* Consent, insurance and pickup all come from the Pickup & consent
          records, so the whole section goes while that switch is off. */}
      {PICKUP_CONSENT_ENABLED && (
        <Section title="Authorization & Insurance">
          <Row
            label="Consent to treat"
            value={
              authorization?.consentToTreat
                ? `Granted${authorization.consentGrantedByName ? ` by ${authorization.consentGrantedByName}` : ""}${
                    authorization.consentGrantedAt
                      ? ` on ${fmtDate(authorization.consentGrantedAt)}`
                      : ""
                  }`
                : "NOT granted"
            }
          />
          <Row label="Insurance carrier" value={dash(authorization?.insuranceCarrier)} />
          <Row label="Policy number" value={dash(authorization?.insurancePolicyNumber)} />
          <Row label="Group number" value={dash(authorization?.insuranceGroupNumber)} />
          <Row
            label="Authorized pickup"
            value={
              pickups && pickups.filter((p) => p.isActive).length > 0
                ? pickups
                    .filter((p) => p.isActive)
                    .map((p) => `${p.name}${p.relationship ? ` (${p.relationship})` : ""}`)
                    .join(", ")
                : "—"
            }
          />
        </Section>
      )}

      {(child.additionalNotes || history?.notes) && (
        <Section title="Additional Notes">
          <p className="whitespace-pre-wrap text-xs">
            {[child.additionalNotes, history?.notes].filter(Boolean).join("\n\n")}
          </p>
        </Section>
      )}

      <p className="mt-4 text-[10px] text-neutral-500">
        This form summarizes information supplied by the child&apos;s parent or
        guardian. It is not a substitute for a clinical record.
      </p>
    </div>
  );
}
